import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Snackbar,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import BuildIcon from '@mui/icons-material/Build';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { formatDate, appConfig } from '@/config/app-config';
import { getErrorMessage } from '@/utils/error';
import { useAuth } from '@/hooks/useAuth';
import type { SyncStatusResponse, SyncErrorLog } from '@/types';
import { getSyncErrors } from '@/api/client';

interface KendraSyncPanelProps {
  syncStatus: SyncStatusResponse | null;
  isSyncing: boolean;
  isLoadingStatus: boolean;
  error: string | null;
  statusText: string;
  statusColor: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  onStartSync: () => void;
}

/**
 * Kendra同期パネルコンポーネント
 */
const KendraSyncPanel: React.FC<KendraSyncPanelProps> = ({
  syncStatus,
  isSyncing,
  isLoadingStatus,
  error,
  statusText,
  statusColor,
  onStartSync,
}) => {
  const { accessibleFolders, hasGroup } = useAuth();
  const isAdmin = hasGroup('administrators');

  const [showErrorDetails, setShowErrorDetails] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorLogs, setErrorLogs] = useState<SyncErrorLog[]>([]);
  const [isLoadingErrors, setIsLoadingErrors] = useState(false);
  const [errorFetchError, setErrorFetchError] = useState<string | null>(null);
  const [errorSyncInfo, setErrorSyncInfo] = useState<{
    executionId?: string;
    syncStartTime?: string;
    syncEndTime?: string;
    message?: string;
  } | null>(null);
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const [copySnackbarMessage, setCopySnackbarMessage] = useState('');

  // エラーがあるかどうか
  const hasError = syncStatus?.status === 'FAILED' ||
                   (syncStatus?.metrics?.documentsFailed ?? 0) > 0 ||
                   !!syncStatus?.errorMessage ||
                   !!error;

  // エラーログを取得
  const fetchErrorLogs = useCallback(async () => {
    setIsLoadingErrors(true);
    setErrorFetchError(null);

    try {
      const result = await getSyncErrors(appConfig.kendraDataSourceId);

      if (result.success && result.data) {
        const allErrors = result.data.errors;
        // 自グループのフォルダに関するエラーのみに絞り込む
        const myFolderValues = accessibleFolders.map(f => f.value);
        const myErrors = myFolderValues.length > 0
          ? allErrors.filter(err =>
              !err.documentId ||
              myFolderValues.some(v => err.documentId!.includes(v))
            )
          : allErrors;
        const hasOtherGroupErrors = myErrors.length === 0 && allErrors.length > 0;
        setErrorLogs(myErrors);
        setErrorSyncInfo({
          executionId: result.data.executionId,
          syncStartTime: result.data.syncStartTime,
          syncEndTime: result.data.syncEndTime,
          message: hasOtherGroupErrors
            ? '同期エラーがありましたが、あなたのグループのフォルダに問題はありません'
            : result.data.message,
        });
      } else {
        throw new Error(result.error || 'エラーログの取得に失敗しました');
      }
    } catch (err: unknown) {
      console.error('Failed to fetch error logs:', err);
      setErrorFetchError(getErrorMessage(err, 'エラーが発生しました'));
    } finally {
      setIsLoadingErrors(false);
    }
  }, []);

  // エラーダイアログを開く
  const handleOpenErrorDialog = () => {
    setErrorDialogOpen(true);
    fetchErrorLogs();
  };

  // ファイル名を抽出（パスから）
  const extractFileName = (path?: string) => {
    if (!path) return '不明';
    const parts = path.split('/');
    return parts[parts.length - 1] || path;
  };

  // エラーメッセージを短縮
  const truncateMessage = (message: string, maxLength: number = 80) => {
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + '...';
  };

  // クリップボードにコピー
  const copyToClipboard = useCallback(async (text: string, successMessage: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySnackbarMessage(successMessage);
    } catch {
      setCopySnackbarMessage('コピーに失敗しました');
    }
    setCopySnackbarOpen(true);
  }, []);

  // 1件のエラーログをテキスト化
  const formatErrorLine = useCallback((log: SyncErrorLog): string => {
    const parts = [
      `時刻: ${formatDate(log.timestamp)}`,
      `ファイル: ${log.documentId || '不明'}`,
    ];
    if (log.errorCode) {
      parts.push(`エラーコード: ${log.errorCode}`);
    }
    parts.push(`エラー内容: ${log.message}`);
    return parts.join('\n');
  }, []);

  // 全エラーログをテキスト化してコピー
  const handleCopyAllErrors = useCallback(() => {
    const header = [
      errorSyncInfo?.syncStartTime ? `同期開始: ${formatDate(errorSyncInfo.syncStartTime)}` : '',
      errorSyncInfo?.syncEndTime ? `同期終了: ${formatDate(errorSyncInfo.syncEndTime)}` : '',
      errorSyncInfo?.executionId ? `実行ID: ${errorSyncInfo.executionId}` : '',
    ].filter(Boolean).join('\n');

    const body = errorLogs.map(formatErrorLine).join('\n\n');
    const text = header ? `${header}\n\n${body}` : body;
    copyToClipboard(text, `${errorLogs.length}件のエラーをコピーしました`);
  }, [errorLogs, errorSyncInfo, formatErrorLine, copyToClipboard]);

  if (!isAdmin) {
    return (
      <Paper sx={{ p: 2, opacity: 0.6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <BuildIcon fontSize="small" color="disabled" />
          <Typography variant="subtitle2" fontWeight="bold" color="text.disabled">
            Kendra同期
          </Typography>
        </Box>
        <Alert severity="info" icon={false}>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            この機能は現在準備中です
          </Typography>
          <Typography variant="caption">
            管理者による検証完了後に一般公開予定です。
          </Typography>
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom fontWeight="bold">
        Kendra同期
      </Typography>

      {/* ステータス表示 */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            ステータス:
          </Typography>
          {isLoadingStatus ? (
            <CircularProgress size={16} />
          ) : (
            <Chip
              label={statusText}
              color={statusColor}
              size="small"
            />
          )}
        </Box>

        {syncStatus?.startTime && (
          <Typography variant="caption" color="text.secondary" display="block">
            開始: {formatDate(syncStatus.startTime)}
          </Typography>
        )}
        {syncStatus?.endTime && (
          <Typography variant="caption" color="text.secondary" display="block">
            終了: {formatDate(syncStatus.endTime)}
          </Typography>
        )}
      </Box>

      {/* 同期メトリクス */}
      {syncStatus?.metrics && (
        <>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
              最終同期結果:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              <Chip
                label={`追加: ${syncStatus.metrics.documentsAdded}`}
                size="small"
                variant="outlined"
                color="success"
              />
              <Chip
                label={`更新: ${syncStatus.metrics.documentsModified}`}
                size="small"
                variant="outlined"
                color="info"
              />
              <Chip
                label={`削除: ${syncStatus.metrics.documentsDeleted}`}
                size="small"
                variant="outlined"
                color="warning"
              />
            </Box>

            {/* 失敗したドキュメント（強調表示） */}
            {syncStatus.metrics.documentsFailed > 0 && (
              <Box sx={{ mt: 1 }}>
                <Chip
                  icon={<ErrorOutlineIcon />}
                  label={`失敗: ${syncStatus.metrics.documentsFailed}件`}
                  size="small"
                  color="error"
                  onClick={handleOpenErrorDialog}
                  sx={{ cursor: 'pointer' }}
                />
                <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                  クリックしてエラー詳細を表示
                </Typography>
              </Box>
            )}
          </Box>
        </>
      )}

      {/* エラーメッセージ（折りたたみ可能） */}
      {hasError && (
        <Box sx={{ mb: 2 }}>
          <Alert
            severity="error"
            action={
              <IconButton
                size="small"
                onClick={() => setShowErrorDetails(!showErrorDetails)}
              >
                {showErrorDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            }
          >
            <Box>
              <Typography variant="body2" fontWeight="bold">
                同期エラーが発生しました
              </Typography>
              {!showErrorDetails && (
                <Typography variant="caption">
                  詳細を表示するには展開してください
                </Typography>
              )}
            </Box>
          </Alert>

          <Collapse in={showErrorDetails}>
            <Box sx={{ mt: 1, p: 2, bgcolor: 'error.50', borderRadius: 1 }}>
              {/* APIエラー */}
              {error && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" fontWeight="bold" color="error">
                    API エラー:
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                    {error}
                  </Typography>
                </Box>
              )}

              {/* Kendraエラーメッセージ */}
              {syncStatus?.errorMessage && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" fontWeight="bold" color="error">
                    Kendra エラー:
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 0.5, whiteSpace: 'pre-wrap' }}>
                    {syncStatus.errorMessage}
                  </Typography>
                </Box>
              )}

              {/* エラー詳細確認ボタン */}
              <Button
                variant="outlined"
                size="small"
                color="error"
                startIcon={<ErrorOutlineIcon />}
                onClick={handleOpenErrorDialog}
              >
                エラー詳細を表示
              </Button>
            </Box>
          </Collapse>
        </Box>
      )}

      {/* 同期ボタン */}
      <Button
        variant="contained"
        fullWidth
        startIcon={isSyncing ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
        onClick={onStartSync}
        disabled={isSyncing}
      >
        {isSyncing ? '同期中...' : 'Sync Now'}
      </Button>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        S3のファイルをKendraインデックスに同期します
      </Typography>

      {/* エラー詳細ダイアログ */}
      <Dialog
        open={errorDialogOpen}
        onClose={() => setErrorDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ErrorOutlineIcon color="error" />
            同期エラー詳細
          </Box>
          <IconButton
            size="small"
            onClick={fetchErrorLogs}
            disabled={isLoadingErrors}
          >
            <RefreshIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {isLoadingErrors ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : errorFetchError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorFetchError}
            </Alert>
          ) : errorSyncInfo?.message && errorLogs.length === 0 ? (
            <Alert severity="info">
              {errorSyncInfo.message}
            </Alert>
          ) : errorLogs.length === 0 ? (
            <Alert severity="info">
              エラーログが見つかりませんでした。
            </Alert>
          ) : (
            <>
              {/* 同期ジョブ情報 */}
              <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                  <Typography variant="body2" fontWeight="bold" gutterBottom>
                    最後の同期ジョブのエラー（{errorLogs.length}件）
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ContentCopyIcon fontSize="small" />}
                    onClick={handleCopyAllErrors}
                    sx={{ flexShrink: 0 }}
                  >
                    全件コピー
                  </Button>
                </Box>
                {errorSyncInfo?.syncStartTime && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    同期開始: {formatDate(errorSyncInfo.syncStartTime)}
                  </Typography>
                )}
                {errorSyncInfo?.syncEndTime && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    同期終了: {formatDate(errorSyncInfo.syncEndTime)}
                  </Typography>
                )}
                {errorSyncInfo?.executionId && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    実行ID: {errorSyncInfo.executionId}
                  </Typography>
                )}
              </Box>

              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', width: '18%' }}>時刻</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '27%' }}>ファイル</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '45%' }}>エラー内容</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '10%' }} align="center">操作</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {errorLogs.map((log, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Typography variant="caption">
                            {formatDate(log.timestamp)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={log.documentId || '不明'}>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                              {extractFileName(log.documentId)}
                            </Typography>
                          </Tooltip>
                          {log.errorCode && (
                            <Chip
                              label={log.errorCode}
                              size="small"
                              color="error"
                              variant="outlined"
                              sx={{ mt: 0.5 }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip title={log.message}>
                            <Typography variant="body2">
                              {truncateMessage(log.message)}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="このエラーをコピー">
                            <IconButton
                              size="small"
                              onClick={() => copyToClipboard(formatErrorLine(log), 'エラーをコピーしました')}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              よくあるエラー原因:
            </Typography>
            <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
              <li>ファイルサイズが大きすぎる（上限: 50MB）</li>
              <li>サポートされていないファイル形式</li>
              <li>ファイルが破損している</li>
              <li>S3アクセス権限の問題</li>
              <li>ファイルのエンコーディング問題</li>
            </ul>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorDialogOpen(false)}>
            閉じる
          </Button>
        </DialogActions>
      </Dialog>

      {/* コピー完了通知 */}
      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={2000}
        onClose={() => setCopySnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={copySnackbarMessage}
      />
    </Paper>
  );
};

export default KendraSyncPanel;
